/**
 * Endoscopy reading room — the endoscopist's primary worklist surface.
 *
 * Single-modality product (endopod = endoscopy): this is the endoscopy
 * reader's queue.  The route is /reading; the user calls this
 * the "Endoscopy" tab.  Tabs scope the rows (mine / unread / stat
 * / all); the table chrome (paginator, sortable headers, per-user
 * column-visibility prefs) is the shared DataTable component, so
 * the look and feel matches Worklist and Reports.
 *
 * Issue #5 follow-up (2026-05-20) — refit with DataTable so the
 * three reader-facing lists (Worklist, Reading, Reports) share
 * pagination, sort, and persisted column prefs.  The previous
 * j/k/Enter keyboard nav is dropped in this revision; the
 * shortcut is documented in ShortcutHelp and will return as a
 * follow-up when DataTable exposes a focused-row API.
 */
import { createMemo, createSignal, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { createQuery } from "@tanstack/solid-query";
import { listStudies, type Study } from "../api/client";
import { auth } from "../auth/store";
import { modalitiesInScope, singleSpecialty } from "../auth/scope";
import { SPECIALTY_CONFIGS } from "../specialty/config";
import { DataTable } from "../components/DataTable";
import type { DataColumn } from "../components/DataTable";
import { caseId } from "./caseId";
import { fetchStudySources, sourceLabel } from "./studySources";

type Tab = "mine" | "unread" | "stat" | "all";

interface StudyCounts {
  video_count: number;
  image_count: number;
}

async function fetchStudyCounts(uids: string[]): Promise<Record<string, StudyCounts>> {
  if (uids.length === 0) return {};
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const out: Record<string, StudyCounts> = {};
  for (let i = 0; i < uids.length; i += 500) {
    const slice = uids.slice(i, i + 500);
    const rsp = await fetch("/viewer-api/studies/counts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${a?.accessToken ?? ""}` },
      body: JSON.stringify({ uids: slice }),
    });
    if (!rsp.ok) continue;
    Object.assign(out, await rsp.json() as Record<string, StudyCounts>);
  }
  return out;
}

export function EndoscopyReadingPage() {
  const nav = useNavigate();
  const [tab, setTab] = createSignal<Tab>("unread");
  const [q,   setQ]   = createSignal("");

  // Fetch the scoped list ONCE and filter client-side (below) across all
  // columns — same as Worklist/Studies. Previously the search box was
  // sent to the server as a QIDO PatientName filter, so typing anything
  // that isn't a patient name (Case ID, accession, …) returned zero rows
  // and nothing could be filtered.
  const studies = createQuery(() => ({
    queryKey: ["studies", "reading"],
    queryFn:  () => listStudies({}),
  }));

  const meSub = () => auth()?.claims.sub ?? "";

  const scopeModalities = createMemo(() => modalitiesInScope(auth()));
  const soleSpec        = createMemo(() => singleSpecialty(auth()));

  const filtered = createMemo<Study[]>(() => {
    const rows = studies.data ?? [];
    const allowed = scopeModalities();
    let scoped = allowed
      ? rows.filter((s) => s.modality && allowed.has(s.modality.toUpperCase()))
      : rows;
    const needle = q().trim().toLowerCase();
    if (needle) {
      scoped = scoped.filter((s) => {
        const hay = [caseId(s.studyUid), s.patientName, s.patientId, s.accession, s.studyUid, s.description]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(needle);
      });
    }
    switch (tab()) {
      case "mine":   return scoped.filter((s) => (s.assignedTo ?? "") === meSub());
      case "unread": return scoped.filter((s) => !s.reportSigned);
      case "stat":   return scoped.filter((s) => (s.priority ?? "") === "STAT");
      default:       return scoped;
    }
  });

  // Batched Videos / Images counts for whatever the QIDO list returned.
  // Keyed on the full UID set so a refetch re-runs counts in lockstep.
  const allUids = createMemo(() => (studies.data ?? []).map((s) => s.studyUid));
  const counts = createQuery(() => ({
    queryKey: ["study-counts", allUids().join(",")],
    queryFn:  () => fetchStudyCounts(allUids()),
    enabled:  allUids().length > 0,
    staleTime: 60_000,
  }));
  function countsFor(uid: string): StudyCounts {
    return counts.data?.[uid] ?? { video_count: 0, image_count: 0 };
  }
  // Which EndoEdge box uploaded each study (resolved to site name).
  const sources = createQuery(() => ({
    queryKey: ["study-sources", allUids().join(",")],
    queryFn:  () => fetchStudySources(allUids()),
    enabled:  allUids().length > 0,
    staleTime: 60_000,
  }));

  // Default visible set per issue #5 follow-up: Patient, MRN,
  // Videos, Images, When (Date), Priority, Report.  Other columns
  // accessible via Columns ▾ and the choice persists in user_prefs.
  const columns: DataColumn<Study>[] = [
    {
      id: "case", label: "Case ID",
      render: (s) => <span class="font-mono text-ink-900" title={s.studyUid}>{caseId(s.studyUid)}</span>,
      sortValue: (s) => caseId(s.studyUid),
      alwaysVisible: true,
    },
    {
      id: "patient", label: "Patient",
      render: (s) => (
        <span class="font-medium text-white">
          {s.patientName ?? "—"}
        </span>
      ),
      sortValue: (s) => (s.patientName ?? "").toLowerCase(),
      alwaysVisible: true,
    },
    {
      id: "mrn", label: "MRN",
      render: (s) => <span class="font-mono text-ink-700">{s.patientId}</span>,
      sortValue: (s) => (s.patientId ?? "").toLowerCase(),
    },
    {
      id: "source", label: "EndoEdge box",
      render: (s) => <span class="text-ink-700">{sourceLabel(sources.data?.[s.studyUid])}</span>,
      sortValue: (s) => sourceLabel(sources.data?.[s.studyUid]),
      alwaysVisible: true,
    },
    {
      id: "accession", label: "Accession",
      render: (s) => <span>{s.accession || "—"}</span>,
      sortValue: (s) => (s.accession ?? "").toLowerCase(),
      defaultVisible: false,
    },
    {
      id: "date", label: "Date",
      render: (s) => <span>{fmtDate(s.studyDate)}</span>,
      sortValue: (s) => s.studyDate ?? "",
    },
    {
      id: "modality", label: "Modality",
      render: (s) => <span class="chip-brand">{s.modality}</span>,
      sortValue: (s) => s.modality ?? "",
      defaultVisible: false,             // single-modality product
    },
    {
      id: "videos", label: "Videos",
      render: (s) => {
        const c = countsFor(s.studyUid).video_count;
        return <span class={c ? "font-mono text-ink-900" : "font-mono text-ink-600"}>{c}</span>;
      },
      sortValue: (s) => countsFor(s.studyUid).video_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "images", label: "Images",
      render: (s) => {
        const c = countsFor(s.studyUid).image_count;
        return <span class={c ? "font-mono text-ink-900" : "font-mono text-ink-600"}>{c}</span>;
      },
      sortValue: (s) => countsFor(s.studyUid).image_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "priority", label: "Priority",
      render: (s) => (
        <Show when={(s.priority ?? "ROUTINE") === "STAT"}
              fallback={<span class="text-ink-700">routine</span>}>
          <span class="chip-danger">STAT</span>
        </Show>
      ),
      sortValue: (s) => s.priority ?? "ROUTINE",
    },
    {
      id: "report", label: "Report",
      render: (s) => (
        <Show when={s.reportSigned}
              fallback={<span class="text-ink-700">unread</span>}>
          <span class="chip-ok">signed</span>
        </Show>
      ),
      sortValue: (s) => s.reportSigned ? 1 : 0,
    },
    {
      id: "description", label: "Description",
      render: (s) => <span class="truncate text-ink-700">{s.description ?? "—"}</span>,
      sortValue: (s) => (s.description ?? "").toLowerCase(),
      defaultVisible: false,
    },
  ];

  return (
    <div class="flex h-full flex-col">
      <Show when={soleSpec()}>
        <div class="flex items-center gap-2 border-b border-brand/40 bg-brand/10 px-4 py-1.5 text-[11px]">
          <span class="chip-brand !text-[9px]">
            {SPECIALTY_CONFIGS[soleSpec()!].label.toUpperCase()}
          </span>
          <span class="text-ink-900">
            Reading-room worklist scoped to <span class="font-mono">{SPECIALTY_CONFIGS[soleSpec()!].label.toLowerCase()}</span> studies.
          </span>
        </div>
      </Show>
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Reading room</span>
        <TabBtn current={tab()} value="mine"   label="Assigned to me" onSelect={setTab} />
        <TabBtn current={tab()} value="unread" label="Unread"         onSelect={setTab} />
        <TabBtn current={tab()} value="stat"   label="STAT"           onSelect={setTab} />
        <TabBtn current={tab()} value="all"    label="All"            onSelect={setTab} />
        <div class="min-w-[240px] flex-1">
          <input type="search"
                 placeholder="Patient / MRN / accession…"
                 class="w-full rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-sm"
                 value={q()}
                 onInput={(e) => setQ(e.currentTarget.value)} />
        </div>
        <span class="font-mono text-[11px] text-ink-700">
          {studies.isLoading ? "loading…" : `${filtered().length} studies`}
        </span>
      </header>

      <Show when={studies.isError}>
        <div class="m-4 rounded border border-danger/50 bg-danger/10 p-3 text-sm text-danger">
          QIDO call failed — {String(studies.error)}
        </div>
      </Show>

      <div class="flex-1 min-h-0 p-3">
        <DataTable<Study>
          tableId="reading"
          rows={filtered}
          columns={columns}
          rowKey={(s) => s.studyUid}
          onRowClick={(s) => nav(`/study/${encodeURIComponent(s.studyUid)}`)}
          defaultSort={{ col: "date", dir: "desc" }}
          defaultPageSize={50}
          loading={() => studies.isLoading}
          emptyState={<span>No studies match the current filter.</span>}
        />
      </div>
    </div>
  );
}

function TabBtn(p: { current: Tab; value: Tab; label: string; onSelect: (v: Tab) => void }) {
  return (
    <button type="button"
            class="rounded border border-ink-300 px-2 py-1 text-[11px] uppercase tracking-wider"
            classList={{
              "bg-brand text-white border-brand": p.current === p.value,
              "text-ink-700 hover:text-white":    p.current !== p.value,
            }}
            onClick={() => p.onSelect(p.value)}>
      {p.label}
    </button>
  );
}

function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
